/**
 * Priority+ navigation.
 *
 * The header bar shows as many top-level links as genuinely fit; the rest
 * move into a "More" dropdown at the end. Recalculated on resize and after
 * fonts load, since a webfont swap changes every label's width.
 *
 * Why this rather than scrolling the bar: a scrolling container clips its
 * children, which killed the dropdown panels. This keeps overflow visible
 * so panels can hang below the header, and it adapts to any menu length
 * without anyone having to curate one.
 *
 * Items that own a dropdown are pinned — moving a mega panel inside another
 * dropdown would nest two panels, so those always keep their place on the bar.
 */
(() => {
  const nav = document.querySelector('[data-nav-overflow]');
  if (!nav) return;

  const list = nav.querySelector('.header__menu-list');
  const more = nav.querySelector('[data-nav-more]');
  if (!list || !more) return;

  const moreList = more.querySelector('.header__dropdown-list');
  const items = [...list.querySelectorAll('.header__menu-item:not([data-nav-more])')];

  // Pinned items never move: they carry their own dropdown panel.
  const movable = items.filter((li) => !li.classList.contains('has-children'));

  /**
   * Sum the visible items rather than reading list.scrollWidth.
   *
   * scrollWidth counts absolutely-positioned descendants that overflow, and
   * the dropdown panels are ~966px wide. Measuring it made the bar look
   * permanently over-full and the script stripped it down to two links.
   */
  const usedWidth = () => {
    const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
    const shown = [...list.querySelectorAll('.header__menu-item')].filter((li) => !li.hidden);
    if (!shown.length) return 0;
    const total = shown.reduce((sum, li) => sum + li.getBoundingClientRect().width, 0);
    return total + gap * (shown.length - 1);
  };

  // A little slack so the last item never sits flush against the icons.
  const fits = () => usedWidth() <= nav.clientWidth - 8;

  const layout = () => {
    // Start from everything on the bar, in original order.
    movable.forEach((li) => {
      li.hidden = false;
      const stashed = li.dataset.stashed;
      if (stashed) delete li.dataset.stashed;
    });
    moreList.innerHTML = '';
    more.hidden = true;

    if (fits()) return;

    more.hidden = false;

    // Move from the end until the bar fits, leaving at least two on the bar
    // so it does not collapse to just "More".
    for (let i = movable.length - 1; i >= 0 && !fits(); i--) {
      const li = movable[i];
      if (list.querySelectorAll('.header__menu-item:not([hidden]):not([data-nav-more])').length <= 2) break;
      li.hidden = true;
      li.dataset.stashed = '1';
    }

    // Rebuild the More panel in original order.
    movable
      .filter((li) => li.dataset.stashed)
      .forEach((li) => {
        const a = li.querySelector('a');
        const clone = document.createElement('li');
        const link = document.createElement('a');
        link.href = a.getAttribute('href');
        link.textContent = a.textContent.trim();
        if (a.hasAttribute('aria-current')) link.setAttribute('aria-current', 'page');
        clone.appendChild(link);
        moreList.appendChild(clone);
      });

    // Nothing actually overflowed after all.
    if (!moreList.children.length) more.hidden = true;
  };

  layout();

  let frame;
  addEventListener('resize', () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(layout);
  });

  // Webfonts change label widths; recompute once they are in.
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(layout);
})();
